<!-- packages/studio/src/components/army/BattleProfileForm.vue -->
<!-- The unit's list-construction layer (battle profile). Off by default: the unit
     inherits the published (PDF) battle profile at merge. Turning it on authors an
     override — chiefly the regiment composition (who this leader can take), plus the
     unit's sub-hero categories and any required-leader (companion) rule. The
     duplicated warscroll fields (points/size/keywords/…) are derived from the unit at
     merge, so they aren't edited here. -->
<template>
  <div class="bp-form">
    <StField
      label="Custom battle profile"
      inline
      hint="Off: inherit the published profile. On: author this unit's list-construction rules."
    >
      <StCheckbox :model-value="enabled" @update:model-value="toggle">
        Override the published battle profile
      </StCheckbox>
    </StField>

    <template v-if="enabled && profile">
      <section class="form-section">
        <div class="st-eyebrow">Regiment options</div>
        <p class="bp-form__hint">
          Which units this unit can take in its regiment. Each option allows a number of units
          matching the keywords / sub-hero categories / named units.
        </p>
        <EditableList
          :items="profile.regiment_options"
          add-label="Add option"
          remove-label="Remove option"
          @add="addOption"
          @remove="removeOption"
        >
          <template #row="{ index }">
            <div class="bp-opt">
              <div class="bp-opt__counts">
                <StField label="Min">
                  <StNumberInput
                    :model-value="profile.regiment_options[index].min"
                    :min="0"
                    @update:model-value="patchOption(index, { min: $event ?? 0 })"
                  />
                </StField>
                <StField label="Max" hint="-1 = any">
                  <StNumberInput
                    :model-value="profile.regiment_options[index].max"
                    :min="-1"
                    @update:model-value="patchOption(index, { max: $event ?? 0 })"
                  />
                </StField>
              </div>
              <StField group label="Required keywords">
                <KeywordsInput
                  raw
                  uppercase
                  suggestion-scope="unit"
                  placeholder="e.g. INFANTRY"
                  :model-value="profile.regiment_options[index].keywords"
                  @update:model-value="patchOption(index, { keywords: $event })"
                />
              </StField>
              <StField group label="Excluded keywords">
                <KeywordsInput
                  raw
                  uppercase
                  suggestion-scope="unit"
                  placeholder="e.g. HERO"
                  :model-value="profile.regiment_options[index].nonKeywords"
                  @update:model-value="patchOption(index, { nonKeywords: $event })"
                />
              </StField>
              <StField group label="Sub-hero categories">
                <KeywordsInput
                  raw
                  noun="Category"
                  placeholder="e.g. Bloodbound Warmonger"
                  :model-value="profile.regiment_options[index].subhero_categories"
                  @update:model-value="patchOption(index, { subhero_categories: $event })"
                />
              </StField>
              <StField group label="Unit names">
                <KeywordsInput
                  raw
                  noun="Unit"
                  placeholder="e.g. Liberators"
                  :model-value="profile.regiment_options[index].unit_names"
                  @update:model-value="patchOption(index, { unit_names: $event })"
                />
              </StField>
            </div>
          </template>
        </EditableList>
      </section>

      <StField
        group
        label="Sub-hero categories"
        hint="Categories this unit belongs to (so leaders can take it)"
      >
        <KeywordsInput
          raw
          noun="Category"
          placeholder="e.g. Bloodbound Warmonger"
          :model-value="profile.subhero_categories"
          @update:model-value="patch({ subhero_categories: $event })"
        />
      </StField>

      <StField
        label="Required leader"
        hint="If this is a companion unit, the leader it must be taken with"
      >
        <StTextInput
          :model-value="profile.requiredLeader"
          placeholder="Leader name"
          @update:model-value="patch({ requiredLeader: $event ?? '' })"
        />
      </StField>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { IUnit, IApdBattleProfile, IApdRegimentOptions } from '@scrolldex/shared';
import { makeBattleProfile, makeRegimentOption } from '@scrolldex/shared';
import KeywordsInput from '../KeywordsInput.vue';
import EditableList from '../EditableList.vue';
import { StField, StCheckbox, StNumberInput, StTextInput } from '../ui/index.js';

const props = defineProps<{ modelValue: IUnit }>();
const emit = defineEmits<{ (e: 'update:modelValue', value: IUnit): void }>();

const enabled = computed(() => props.modelValue.battleProfile != null);
const profile = computed(() => props.modelValue.battleProfile);

function patchUnit(part: Partial<IUnit>) {
  emit('update:modelValue', { ...props.modelValue, ...part });
}
// On: materialize an override seeded from the unit. Off: drop back to inheriting.
function toggle(on: boolean) {
  patchUnit({ battleProfile: on ? makeBattleProfile(props.modelValue) : null });
}
function patch(part: Partial<IApdBattleProfile>) {
  if (!profile.value) return;
  patchUnit({ battleProfile: { ...profile.value, ...part } });
}
function patchOption(index: number, part: Partial<IApdRegimentOptions>) {
  if (!profile.value) return;
  patch({
    regiment_options: profile.value.regiment_options.map((o, i) =>
      i === index ? { ...o, ...part } : o,
    ),
  });
}
function addOption() {
  if (!profile.value) return;
  patch({ regiment_options: [...profile.value.regiment_options, makeRegimentOption()] });
}
function removeOption(index: number) {
  if (!profile.value) return;
  patch({ regiment_options: profile.value.regiment_options.filter((_, i) => i !== index) });
}
</script>

<style scoped>
.bp-form {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.bp-form__hint {
  font-size: var(--st-text-sm);
  color: var(--st-text-muted);
  max-width: 44rem;
  line-height: 1.5;
}
.bp-opt {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.bp-opt__counts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--st-space-2);
}
</style>
